import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckCircle, XCircle, Settings, RefreshCw, UtensilsCrossed, Images, Star, Gift, Clock, AlertTriangle, LogOut, Printer, Utensils, Info, Send, ChefHat, FileBarChart, Mail, FileText, BarChart3 } from "lucide-react";
import { API } from "@/App";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/AdminAuthContext";

// Restaurant Opening Hours Configuration
const OPENING_HOURS = {
  0: { open: 15, close: 22 },  // Sunday
  1: { open: 15, close: 22 },  // Monday
  2: { open: 15, close: 22 },  // Tuesday
  3: { open: 15, close: 22 },  // Wednesday
  4: { open: 15, close: 22 },  // Thursday
  5: { open: 15, close: 23 },  // Friday
  6: { open: 15, close: 23 },  // Saturday
};

const ORDER_ACCEPT_TIMEOUT = 3 * 60 * 1000;  // 3 minutes in ms
const WARNING_THRESHOLD = 1.5 * 60 * 1000;   // 1.5 minutes in ms
const TAKEAWAY_CUTOFF_MINUTES = 30;          // 30 minutes before closing

// Helper function to check if restaurant is open
const isRestaurantOpen = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const hours = OPENING_HOURS[dayOfWeek];
  
  if (!hours) return false;
  
  const currentTime = currentHour + currentMinute / 60;
  return currentTime >= hours.open && currentTime < hours.close;
};

// Helper function to get closing time for today
const getClosingTime = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const hours = OPENING_HOURS[dayOfWeek];
  return hours ? hours.close : null;
};

// Helper function to check if takeaway orders should be accepted
const canAcceptTakeaway = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const hours = OPENING_HOURS[dayOfWeek];
  
  if (!hours) return false;
  
  const closingTime = new Date();
  closingTime.setHours(hours.close, 0, 0, 0);
  
  const minutesUntilClose = (closingTime - now) / (1000 * 60);
  return minutesUntilClose > TAKEAWAY_CUTOFF_MINUTES;
};

// Helper to format time remaining
const formatTimeRemaining = (ms) => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Order Timer Component for individual orders
const OrderTimer = ({ order, onWarning, isOpen }) => {
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [hasWarned, setHasWarned] = useState(false);
  const warningPlayedRef = useRef(false);
  
  useEffect(() => {
    // Only show timer during opening hours and for 'paid' status orders
    if (!isOpen || order.status !== 'paid') {
      setTimeRemaining(null);
      return;
    }
    
    const orderTime = new Date(order.created_at).getTime();
    const now = Date.now();
    const elapsed = now - orderTime;
    const remaining = ORDER_ACCEPT_TIMEOUT - elapsed;
    
    if (remaining <= 0) {
      setTimeRemaining(0);
      return;
    }
    
    setTimeRemaining(remaining);
    
    const interval = setInterval(() => {
      const currentTime = Date.now();
      const newElapsed = currentTime - orderTime;
      const newRemaining = ORDER_ACCEPT_TIMEOUT - newElapsed;
      
      if (newRemaining <= 0) {
        setTimeRemaining(0);
        clearInterval(interval);
        return;
      }
      
      setTimeRemaining(newRemaining);
      
      // Trigger warning at 1.5 minutes remaining
      if (newRemaining <= WARNING_THRESHOLD && !warningPlayedRef.current) {
        warningPlayedRef.current = true;
        setHasWarned(true);
        onWarning(order);
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [order, isOpen, onWarning]);
  
  // Don't show timer outside opening hours
  if (!isOpen || order.status !== 'paid') return null;
  
  if (timeRemaining === null) return null;
  
  const isUrgent = timeRemaining <= WARNING_THRESHOLD;
  const isExpired = timeRemaining <= 0;
  
  return (
    <div 
      className={`flex items-center gap-2 px-3 py-2 rounded-lg mt-2 ${isExpired ? 'animate-pulse' : ''}`}
      style={{ 
        background: isExpired ? '#ef4444' : isUrgent ? '#f59e0b' : '#3b82f6',
        color: 'white'
      }}
      data-testid={`order-timer-${order.order_number}`}
    >
      <Clock className="h-4 w-4" />
      <span className="font-bold text-sm">
        {isExpired ? 'TIDEN ER UTE!' : `${formatTimeRemaining(timeRemaining)} igjen for å godkjenne`}
      </span>
      {isUrgent && !isExpired && <AlertTriangle className="h-4 w-4 animate-bounce" />}
    </div>
  );
};

const AdminPage = () => {
  const [orders, setOrders] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOrderSound, setNewOrderSound] = useState(null);
  const [lastOrderCount, setLastOrderCount] = useState(0);
  const [isRestaurantCurrentlyOpen, setIsRestaurantCurrentlyOpen] = useState(isRestaurantOpen());
  const [canTakeaway, setCanTakeaway] = useState(canAcceptTakeaway());
  const warningSound = useRef(null);
  const { logout, username } = useAdminAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/panel050623/login");
    toast.success("Logget ut");
  };

  // Request browser notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        toast.success('Push-varsler aktivert!');
        localStorage.setItem('push_notifications_enabled', 'true');
      } else {
        toast.error('Push-varsler ble avvist');
      }
    } else {
      toast.error('Nettleseren støtter ikke push-varsler');
    }
  };

  // Send browser notification for new order
  const sendBrowserNotification = (orderCount) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification('🍛 Ny Bestilling!', {
        body: `Du har ${orderCount} nye bestilling(er) som venter!`,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: 'new-order',
        requireInteraction: true
      });
      
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }
  };

  // Create warning sound
  const createWarningSound = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      return () => {
        // More urgent warning sound
        for (let i = 0; i < 3; i++) {
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          oscillator.frequency.value = 1200;
          gainNode.gain.value = 0.4;
          oscillator.start(audioContext.currentTime + i * 0.3);
          oscillator.stop(audioContext.currentTime + 0.15 + i * 0.3);
        }
      };
    } catch (e) {
      return null;
    }
  }, []);

  // Handle timer warning
  const handleOrderWarning = useCallback((order) => {
    // Play warning sound
    if (warningSound.current) {
      try {
        warningSound.current();
      } catch (e) {
      }
    }
    
    // Show urgent toast
    toast.warning(`⚠️ HASTER! Bestilling #${order.order_number} har kun 1.5 min igjen!`, {
      duration: 15000,
      style: {
        background: '#f59e0b',
        color: 'white',
        fontSize: '16px',
        fontWeight: 'bold',
      }
    });
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    
    // Check restaurant status every minute
    const statusInterval = setInterval(() => {
      setIsRestaurantCurrentlyOpen(isRestaurantOpen());
      setCanTakeaway(canAcceptTakeaway());
    }, 60000);
    
    const createNotificationSound = () => {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      return () => {
        const oscillator1 = audioContext.createOscillator();
        const gainNode1 = audioContext.createGain();
        oscillator1.connect(gainNode1);
        gainNode1.connect(audioContext.destination);
        oscillator1.frequency.value = 800;
        gainNode1.gain.value = 0.3;
        oscillator1.start(audioContext.currentTime);
        oscillator1.stop(audioContext.currentTime + 0.15);
        
        const oscillator2 = audioContext.createOscillator();
        const gainNode2 = audioContext.createGain();
        oscillator2.connect(gainNode2);
        gainNode2.connect(audioContext.destination);
        oscillator2.frequency.value = 800;
        gainNode2.gain.value = 0.3;
        oscillator2.start(audioContext.currentTime + 0.2);
        oscillator2.stop(audioContext.currentTime + 0.35);
        
        const oscillator3 = audioContext.createOscillator();
        const gainNode3 = audioContext.createGain();
        oscillator3.connect(gainNode3);
        gainNode3.connect(audioContext.destination);
        oscillator3.frequency.value = 1000;
        gainNode3.gain.value = 0.3;
        oscillator3.start(audioContext.currentTime + 0.4);
        oscillator3.stop(audioContext.currentTime + 0.7);
      };
    };
    
    setNewOrderSound(createNotificationSound());
    warningSound.current = createWarningSound();
    
    return () => {
      clearInterval(interval);
      clearInterval(statusInterval);
    };
  }, [createWarningSound]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
      const [ordersRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/orders`, authHeaders),
        axios.get(`${API}/bookings`, authHeaders),
      ]);
      
      const newOrders = ordersRes.data;
      const paidOrders = newOrders.filter(o => o.status === 'paid').length;
      
      if (lastOrderCount > 0 && paidOrders > lastOrderCount && newOrderSound) {
        try {
          newOrderSound();
          // Send browser notification
          sendBrowserNotification(paidOrders - lastOrderCount);
          
          toast.success("NY BESTILLING MOTTATT!", { 
            duration: 10000,
            style: {
              background: '#22c55e',
              color: 'white',
              fontSize: '18px',
              fontWeight: 'bold',
            }
          });
        } catch (e) {
        }
      }
      
      setLastOrderCount(paidOrders);
      setOrders(newOrders);
      setBookings(bookingsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptOrder = async (orderNumber) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/orders/${orderNumber}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Bestilling godkjent!");
      fetchData();
    } catch (error) {
      console.error("Error accepting order:", error);
      toast.error("Kunne ikke godkjenne bestilling");
    }
  };

  const handleDeclineOrder = async (orderNumber) => {
    const reason = window.prompt("Årsak til avvisning (valgfritt):");
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/orders/${orderNumber}/decline`, null, {
        params: { reason: reason || "" },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Bestilling avvist");
      fetchData();
    } catch (error) {
      console.error("Error declining order:", error);
      toast.error("Kunne ikke avvise bestilling");
    }
  };

  const handleResendEmail = async (orderNumber) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/orders/${orderNumber}/resend-email`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Bekreftelsesmail sendt til kunden!");
    } catch (error) {
      console.error("Error resending email:", error);
      toast.error(error.response?.data?.detail || "Kunne ikke sende e-post");
    }
  };

  const handleResendReceipt = async (orderNumber) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/orders/${orderNumber}/resend-receipt`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Kvittering sendt til kunden!");
    } catch (error) {
      console.error("Error resending receipt:", error);
      toast.error(error.response?.data?.detail || "Kunne ikke sende kvittering");
    }
  };


  const getStatusBadge = (status) => {
    const config = {
      pending: { bg: '#f59e0b', text: '#fff', label: 'Venter' },
      paid: { bg: '#3b82f6', text: '#fff', label: 'Betalt' },
      accepted: { bg: '#22c55e', text: '#fff', label: 'Godkjent' },
      preparing: { bg: '#f97316', text: '#fff', label: 'Tilberedes' },
      ready: { bg: '#8b5cf6', text: '#fff', label: 'Klar' },
      completed: { bg: '#10b981', text: '#fff', label: 'Fullført' },
      declined: { bg: '#ef4444', text: '#fff', label: 'Avvist' },
    };
    const style = config[status] || { bg: '#9ca3af', text: '#fff', label: status };
    return (
      <Badge style={{ background: style.bg, color: style.text }}>
        {style.label}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen" style={{ background: '#f4e7d8' }}>
      <Navbar />

      {/* Header */}
      <div 
        className="py-12 mandala-pattern"
        style={{ background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="indian-divider mb-2"
              >
                <span className="text-sm uppercase tracking-widest" style={{ color: '#d4af37' }}>Admin</span>
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-3xl sm:text-4xl font-serif-display font-semibold italic"
                style={{ color: '#d4af37' }}
                data-testid="admin-page-title"
              >
                Kontrollpanel
              </motion.h1>
            </div>
            <div className="flex gap-3 flex-wrap">
              <Button 
                onClick={fetchData}
                className="rounded-full"
                style={{ background: 'transparent', border: '2px solid #d4af37', color: '#d4af37' }}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Link to="/panel050623/menu">
                <Button 
                  className="rounded-full"
                  style={{ background: '#d4af37', color: '#2d1810' }}
                  data-testid="admin-menu-link"
                >
                  <UtensilsCrossed className="h-4 w-4 mr-2" />
                  Meny
                </Button>
              </Link>
              <Link to="/panel050623/gallery">
                <Button 
                  className="rounded-full"
                  style={{ background: '#d4af37', color: '#2d1810' }}
                  data-testid="admin-gallery-link"
                >
                  <Images className="h-4 w-4 mr-2" />
                  Galleri
                </Button>
              </Link>
              <Link to="/panel050623/reviews">
                <Button 
                  className="rounded-full"
                  style={{ background: '#d4af37', color: '#2d1810' }}
                  data-testid="admin-reviews-link"
                >
                  <Star className="h-4 w-4 mr-2" />
                  Anmeldelser
                </Button>
              </Link>
              <Link to="/panel050623/giftcards">
                <Button 
                  className="rounded-full"
                  style={{ background: '#d4af37', color: '#2d1810' }}
                  data-testid="admin-giftcards-link"
                >
                  <Gift className="h-4 w-4 mr-2" />
                  Gavekort
                </Button>
              </Link>
              <Link to="/panel050623/catering">
                <Button 
                  className="rounded-full"
                  style={{ background: '#d4af37', color: '#2d1810' }}
                  data-testid="admin-catering-link"
                >
                  <Utensils className="h-4 w-4 mr-2" />
                  Catering
                </Button>
              </Link>
              <Link to="/panel050623/about">
                <Button 
                  className="rounded-full"
                  style={{ background: '#8b4513', color: 'white' }}
                  data-testid="admin-about-link"
                >
                  <Info className="h-4 w-4 mr-2" />
                  Om Oss
                </Button>
              </Link>
              <Link to="/panel050623/reports">
                <Button 
                  className="rounded-full"
                  style={{ background: '#1a472a', color: '#22c55e', border: '2px solid #22c55e' }}
                  data-testid="admin-reports-link"
                >
                  <FileBarChart className="h-4 w-4 mr-2" />
                  Rapporter
                </Button>
              </Link>
              <Link to="/panel050623/traffic">
                <Button 
                  className="rounded-full"
                  style={{ background: '#1a2a47', color: '#60a5fa', border: '2px solid #60a5fa' }}
                  data-testid="admin-traffic-link"
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Trafikk
                </Button>
              </Link>
              <Link to="/panel050623/settings">
                <Button 
                  className="rounded-full btn-gold" 
                  data-testid="admin-settings-link"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Innstillinger
                </Button>
              </Link>
              <Link to="/kjokkenet" target="_blank">
                <Button 
                  className="rounded-full"
                  style={{ background: '#721111', color: '#d4af37', border: '2px solid #d4af37' }}
                  data-testid="admin-kitchen-link"
                >
                  <ChefHat className="h-4 w-4 mr-2" />
                  Kjokken Dashboard
                </Button>
              </Link>
              <Button 
                onClick={handleLogout}
                className="rounded-full"
                style={{ background: '#ef4444', color: 'white' }}
                data-testid="admin-logout-button"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logg ut
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Divider */}
      <div className="h-2" style={{ background: 'linear-gradient(90deg, #721111, #d4af37, #721111)' }}></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Restaurant Status Indicator */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 rounded-xl p-4 flex items-center justify-between"
          style={{ 
            background: isRestaurantCurrentlyOpen ? '#dcfce7' : '#fef3c7', 
            border: `2px solid ${isRestaurantCurrentlyOpen ? '#22c55e' : '#f59e0b'}` 
          }}
          data-testid="restaurant-status-banner"
        >
          <div className="flex items-center gap-3">
            <div 
              className={`w-4 h-4 rounded-full ${isRestaurantCurrentlyOpen ? 'bg-green-500 animate-pulse' : 'bg-amber-500'}`}
            ></div>
            <div>
              <h4 className="font-semibold" style={{ color: isRestaurantCurrentlyOpen ? '#166534' : '#92400e' }}>
                {isRestaurantCurrentlyOpen ? '🟢 Restaurant er ÅPEN' : '🟡 Restaurant er STENGT'}
              </h4>
              <p className="text-sm" style={{ color: isRestaurantCurrentlyOpen ? '#15803d' : '#a16207' }}>
                {isRestaurantCurrentlyOpen 
                  ? `Bestillinger har 3 min tidsfrist. Stenger kl ${getClosingTime()}:00` 
                  : 'Bestillinger kan godkjennes uten tidsfrist'
                }
              </p>
            </div>
          </div>
          {isRestaurantCurrentlyOpen && !canTakeaway && (
            <div 
              className="px-3 py-1 rounded-full text-sm font-semibold"
              style={{ background: '#ef4444', color: 'white' }}
            >
              Takeaway lukket (30 min til stenging)
            </div>
          )}
        </motion.div>

        {/* Push Notification Permission Banner */}
        {'Notification' in window && Notification.permission !== 'granted' && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 rounded-xl p-4 flex items-center justify-between"
            style={{ background: '#dbeafe', border: '2px solid #3b82f6' }}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">🔔</span>
              <div>
                <h4 className="font-semibold" style={{ color: '#1e40af' }}>Aktiver push-varsler</h4>
                <p className="text-sm" style={{ color: '#3730a3' }}>
                  Få varsel på skjermen når nye bestillinger kommer - selv når du er på andre nettsider
                </p>
              </div>
            </div>
            <Button
              onClick={requestNotificationPermission}
              className="rounded-full px-6"
              style={{ background: '#3b82f6', color: 'white' }}
              data-testid="enable-notifications-button"
            >
              Aktiver Varsler
            </Button>
          </motion.div>
        )}

        {/* New Orders Alert */}
        {orders.filter(o => o.status === 'paid').length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 rounded-xl p-6 text-center"
            style={{ background: '#22c55e', border: '3px solid #166534' }}
            data-testid="new-orders-alert"
          >
            <div className="flex items-center justify-center gap-3">
              <span className="text-4xl animate-bounce">🔔</span>
              <div>
                <h3 className="text-2xl font-bold text-white">
                  {orders.filter(o => o.status === 'paid').length} Ny{orders.filter(o => o.status === 'paid').length > 1 ? 'e' : ''} Bestilling{orders.filter(o => o.status === 'paid').length > 1 ? 'er' : ''} Venter!
                </h3>
                <p className="text-green-100">Godkjenn eller avvis bestillinger nedenfor</p>
              </div>
            </div>
          </motion.div>
        )}

        <Tabs defaultValue="orders" className="space-y-6">
          <TabsList 
            className="p-1 rounded-xl"
            style={{ background: '#ede1c8', border: '2px solid #b27f47' }}
            data-testid="admin-tabs"
          >
            <TabsTrigger 
              value="orders" 
              className="data-[state=active]:bg-white rounded-lg px-6 py-2" 
              style={{ color: '#2d1810' }}
              data-testid="orders-tab"
            >
              Bestillinger ({orders.length})
            </TabsTrigger>
            <TabsTrigger 
              value="bookings" 
              className="data-[state=active]:bg-white rounded-lg px-6 py-2"
              style={{ color: '#2d1810' }}
              data-testid="bookings-tab"
            >
              Reservasjoner ({bookings.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="orders" data-testid="orders-tab-content">
            {loading ? (
              <div className="text-center py-20">
                <div 
                  className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-r-transparent"
                  style={{ borderColor: '#721111', borderRightColor: 'transparent' }}
                ></div>
                <p className="mt-4" style={{ color: '#4a3728' }}>Laster bestillinger...</p>
              </div>
            ) : orders.length === 0 ? (
              <div 
                className="text-center py-20 bg-white rounded-xl"
                style={{ border: '2px solid #b27f47' }}
              >
                <p style={{ color: '#4a3728' }}>Ingen bestillinger ennå</p>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order, index) => (
                  <motion.div
                    key={order.order_number}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white rounded-xl shadow-lg p-6"
                    style={{ border: order.status === 'paid' ? '3px solid #22c55e' : '2px solid #b27f47' }}
                    data-testid={`order-card-${order.order_number}`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-serif-display font-semibold" style={{ color: '#2d1810' }}>
                          Bestilling #{order.order_number}
                        </h3>
                        <p className="text-sm" style={{ color: '#4a3728' }}>
                          {order.customer_name} • {order.customer_phone}
                        </p>
                        <p className="text-sm" style={{ color: '#4a3728' }}>{order.customer_email}</p>
                        {/* Order time + Pickup time */}
                        <div className="mt-2 flex flex-wrap gap-2">
                          {order.created_at && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                              style={{ background: '#f4e7d8', color: '#8b4513', border: '1px solid #d4af37' }}>
                              <Clock className="h-3 w-3" />
                              Bestilt kl. {new Date(order.created_at).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                          {order.pickup_type === 'scheduled' && order.scheduled_time ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold"
                              style={{ background: '#dbeafe', color: '#1d4ed8', border: '1px solid #3b82f6' }}>
                              <Clock className="h-3 w-3" />
                              Henting: {order.pickup_time || `kl. ${order.scheduled_time}`}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold"
                              style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b' }}>
                              <Clock className="h-3 w-3" />
                              Sa fort som mulig
                            </span>
                          )}
                        </div>
                        {order.payment_status === "pending" && (
                          <div 
                            className="mt-2 inline-block px-3 py-1 text-xs font-semibold rounded-full"
                            style={{ background: '#d4af37', color: '#2d1810' }}
                          >
                            💰 Betal i Restaurant
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        {getStatusBadge(order.status)}
                        <p className="text-2xl font-bold mt-2" style={{ color: '#721111' }}>
                          kr {order.total_amount},-
                        </p>
                      </div>
                    </div>

                    <div className="pt-4 mb-4" style={{ borderTop: '1px solid #b27f47' }}>
                      <h4 className="font-semibold mb-2" style={{ color: '#2d1810' }}>Varer:</h4>
                      <ul className="space-y-1 text-sm" style={{ color: '#4a3728' }}>
                        {order.line_items.map((item, idx) => (
                          <li key={idx}>
                            {item.name} x{item.quantity} - kr {item.total_price},-
                          </li>
                        ))}
                      </ul>
                      {order.customer_notes && (
                        <div 
                          className="mt-3 p-3 rounded-lg"
                          style={{ background: '#f4e7d8', border: '1px solid #d4af37' }}
                        >
                          <p className="text-xs font-semibold mb-1" style={{ color: '#721111' }}>Kundenotater:</p>
                          <p className="text-sm" style={{ color: '#2d1810' }}>{order.customer_notes}</p>
                        </div>
                      )}
                      
                      {/* Order Timer - Only shows during opening hours for pending orders */}
                      <OrderTimer 
                        order={order} 
                        onWarning={handleOrderWarning}
                        isOpen={isRestaurantCurrentlyOpen}
                      />
                    </div>

                    {order.status === "paid" && (
                      <div className="flex gap-3">
                        <Button
                          onClick={() => handleAcceptOrder(order.order_number)}
                          className="flex-1 text-white font-bold rounded-full transition-transform hover:scale-105"
                          style={{ background: '#22c55e' }}
                          data-testid={`accept-order-${order.order_number}`}
                        >
                          <CheckCircle className="h-5 w-5 mr-2" />
                          Godkjenn
                        </Button>
                        <Button
                          onClick={() => handleDeclineOrder(order.order_number)}
                          className="flex-1 font-bold rounded-full transition-transform hover:scale-105"
                          style={{ background: '#ef4444', color: 'white' }}
                          data-testid={`decline-order-${order.order_number}`}
                        >
                          <XCircle className="h-5 w-5 mr-2" />
                          Avvis
                        </Button>
                        <Button
                          onClick={() => window.open(`/panel050623/kitchen-ticket/${order.order_number}`, '_blank')}
                          className="font-bold rounded-full transition-transform hover:scale-105"
                          style={{ background: '#3b82f6', color: 'white' }}
                          data-testid={`print-order-${order.order_number}`}
                        >
                          <Printer className="h-5 w-5" />
                        </Button>
                        {order.customer_email && (
                          <Button
                            onClick={() => handleResendEmail(order.order_number)}
                            className="font-bold rounded-full transition-transform hover:scale-105"
                            style={{ background: '#8b5cf6', color: 'white' }}
                            data-testid={`resend-email-${order.order_number}`}
                            title="Send bekreftelsesmail til kunden"
                          >
                            <Mail className="h-5 w-5" />
                          </Button>
                        )}
                        {order.customer_email && order.payment_method !== 'at_restaurant' && (
                          <Button
                            onClick={() => handleResendReceipt(order.order_number)}
                            className="font-bold rounded-full transition-transform hover:scale-105"
                            style={{ background: '#059669', color: 'white' }}
                            data-testid={`resend-receipt-${order.order_number}`}
                            title="Send kvittering (PDF) til kunden"
                          >
                            <FileText className="h-5 w-5" />
                          </Button>
                        )}
                      </div>
                    )}
                    {order.status !== "paid" && order.customer_email && (
                      <div className="flex gap-3 mt-2">
                        <Button
                          onClick={() => handleResendEmail(order.order_number)}
                          className="font-bold rounded-full transition-transform hover:scale-105 text-sm"
                          style={{ background: '#8b5cf6', color: 'white' }}
                          data-testid={`resend-email-${order.order_number}`}
                          title="Send bekreftelsesmail til kunden"
                        >
                          <Mail className="h-4 w-4 mr-1" />
                          Send e-post
                        </Button>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="bookings" data-testid="bookings-tab-content">
            {loading ? (
              <div className="text-center py-20">
                <div 
                  className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-r-transparent"
                  style={{ borderColor: '#721111', borderRightColor: 'transparent' }}
                ></div>
                <p className="mt-4" style={{ color: '#4a3728' }}>Laster reservasjoner...</p>
              </div>
            ) : bookings.length === 0 ? (
              <div 
                className="text-center py-20 bg-white rounded-xl"
                style={{ border: '2px solid #b27f47' }}
              >
                <p style={{ color: '#4a3728' }}>Ingen reservasjoner ennå</p>
              </div>
            ) : (
              <div className="space-y-4">
                {bookings.map((booking, index) => (
                  <motion.div
                    key={booking.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white rounded-xl shadow-lg p-6"
                    style={{ border: '2px solid #b27f47' }}
                    data-testid={`booking-card-${booking.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-xl font-serif-display font-semibold" style={{ color: '#2d1810' }}>
                          {booking.customer_name}
                        </h3>
                        <p className="text-sm" style={{ color: '#4a3728' }}>
                          {booking.customer_email} • {booking.customer_phone}
                        </p>
                        <div className="mt-3 space-y-1 text-sm" style={{ color: '#4a3728' }}>
                          <p><strong>Dato:</strong> {booking.booking_date} kl. {booking.booking_time}</p>
                          <p><strong>Gjester:</strong> {booking.number_of_guests}</p>
                        </div>
                        {booking.special_requests && (
                          <div 
                            className="mt-3 p-3 rounded-lg"
                            style={{ background: '#f4e7d8', border: '1px solid #d4af37' }}
                          >
                            <p className="text-xs font-semibold mb-1" style={{ color: '#721111' }}>Spesielle Ønsker:</p>
                            <p className="text-sm" style={{ color: '#2d1810' }}>{booking.special_requests}</p>
                          </div>
                        )}
                      </div>
                      <div>{getStatusBadge(booking.status)}</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Footer />
    </div>
  );
};

export default AdminPage;
